import { InlineAlert } from '../../../../components/InlineAlert'
import { KpiTile } from '../../charts/KpiTile'
import { useWidgetData } from '../../widgets/useWidgetData'
import { WidgetFrame } from '../../widgets/WidgetFrame'
import { WIDGET_KEY, type WidgetProps } from '../../widgets/types'
import type { DashboardFilter, StatusCountBucket } from '../../../../types/insights'
import { sumStatusCounts } from './analyticsMappers'

const WIDGET_KEY_SELF = WIDGET_KEY.filingsByStatus

type Kind = 'rnd' | 'film'

function withKind(filter: DashboardFilter, kind: Kind): DashboardFilter {
  return { ...filter, kind }
}

function kindLabel(kind: Kind): string {
  return kind === 'rnd' ? 'R&D' : 'Film & Media'
}

interface KindCountProps {
  filter: DashboardFilter
  skipRefetch: boolean
  kind: Kind
}

/** One half of the split — degrades entirely on its own (AC3.4's "error on either source → contained card per source"). */
function KindCount({ filter, skipRefetch, kind }: KindCountProps) {
  const { status, data, message, retry } = useWidgetData<StatusCountBucket[]>(WIDGET_KEY_SELF, withKind(filter, kind), skipRefetch)

  if (status === 'loading') {
    return (
      <div
        className="iw-skeleton"
        style={{ height: 100, borderRadius: 'var(--iw-radius)' }}
        aria-busy="true"
        aria-label={`${kindLabel(kind)} loading`}
      />
    )
  }
  if (status === 'error') {
    return (
      <InlineAlert
        severity="error"
        action={
          <button type="button" className="iw-btn iw-btn--sm" onClick={retry}>
            Try again
          </button>
        }
      >
        {message ?? `Could not load ${kindLabel(kind)} case counts.`}
      </InlineAlert>
    )
  }
  if (status === 'not_authorized') {
    return <InlineAlert severity="info">{message ?? 'You do not have access to this data.'}</InlineAlert>
  }

  const count = Array.isArray(data) ? sumStatusCounts(data) : 0
  return (
    <KpiTile
      title={kindLabel(kind)}
      value={count === 0 ? `No ${kindLabel(kind)} cases yet` : `${count} case${count === 1 ? '' : 's'}`}
      breakdown={[]}
    />
  )
}

/**
 * IB-282 View 3, AC3.4 — R&D vs Film split (case count by `kind`, `DashboardFilter.kind`, existing
 * field). Two independent fetches of the SAME real `filings-by-status` endpoint — one per `kind`,
 * deliberately overriding the ambient filter's own `kind` (this tile's whole purpose is comparing
 * both at once). Each half degrades entirely independently (loading/error/not_authorized never
 * blanks the sibling half); a tenant with only one kind renders that kind's real `0`-case count as
 * an explicit "No {kind} cases yet" label, never a bare `0` that could be misread as "this kind
 * isn't used here" vs. a genuine zero-case result.
 *
 * Cycle-to-certification duration is explicitly OUT of this AC (parent DoR §3.4, RAID R-21 — View
 * 4's own aging/duration aggregate). The certified-**value** half of AC3.4's "value and case count"
 * wording is a disclosed, named follow-on, not silently dropped: `credit-value-by-jurisdiction-fy`'s
 * zero-fill semantics are keyed on jurisdiction, not `kind`, and a kind-keyed value cross would be
 * new backend-shape work, not a frontend-only reuse — the case-count half ships now.
 */
export function RndFilmSplitWidget({ filter, skipRefetch }: WidgetProps) {
  return (
    <WidgetFrame title="R&D vs Film split" icon="dollar-sign" state="ok">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--iw-space-3)' }}>
        <KindCount filter={filter} skipRefetch={skipRefetch} kind="rnd" />
        <KindCount filter={filter} skipRefetch={skipRefetch} kind="film" />
      </div>
    </WidgetFrame>
  )
}
